
    import {Inject, Options, Vue} from 'vue-property-decorator';
    import VueCropper from 'vue-cropperjs';
    import {AccountSummaryRepositoryAPI} from '@/profile-creation-wizard/components/for-completion/infra/repository/AccountSummaryRepositoryAPI';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import {TrackingRepository} from '@/common/repositories/track/TrackingRepository';
    import {Components} from '@malt/joy-entrypoint';

    @Options({
        name: 'AccountUploadPhotoDialog',
        components: {VueCropper},
    })
    export default class AccountUploadPhotoDialog extends Vue {
        declare $refs: {
            dialogRef: HTMLElement & Components.JoyDialog;
            cropper: any;
        };

        @Inject({from: DI.accountSummaryRepository}) accountSummaryRepository!: AccountSummaryRepositoryAPI;
        @Inject({from: DI.trackingRepository}) trackingRepository!: TrackingRepository;
        file: File | null = null;
        croppedImageDataURL = '';
        base64Img: any = null;
        imageUrl: string | null = null;
        loading = false;
        cropping = false;
        MAX_SIZE = 5000000;
        MIN_WIDTH = 500;
        MIN_HEIGHT = 500;
        blob: Blob | null = null;

        get isButtonDisabled(): boolean {
            return this.file === null || this.loading;
        }

        async open(): Promise<void> {
            await this.$refs.dialogRef.openDialog();
        }

        changePhoto(event: CustomEvent): void {
            const file: File = event.detail.length === 1 ? event.detail[0] : null;
            if (file) {
                const reader = new FileReader();
                reader.onload = (readerEvent: any) => {
                    this.base64Img = new Image();

                    this.base64Img.onload = (img: any) => {
                        if (img.target.width < this.MIN_WIDTH || img.target.height < this.MIN_HEIGHT) {
                            pushVJoySnackbar({
                                props: {
                                    message: this.$msg(
                                        'image.edit.error.message.invalid.dimensions',
                                        img.target.width + '*' + img.target.height,
                                        this.MIN_WIDTH + '*' + this.MIN_HEIGHT,
                                    ),
                                    level: 'error',
                                },
                            });
                        } else {
                            this.file = file;
                            this.imageUrl = URL.createObjectURL(file);
                        }
                    };
                    this.base64Img.src = readerEvent.target.result;
                };
                reader.readAsDataURL(file as Blob);
            }
        }

        clearFile(): void {
            this.file = null;
            this.blob = null;
            this.imageUrl = null;
            this.cropping = false;
        }

        cropFile(): void {
            this.croppedImageDataURL = '';
            this.cropping = true;
        }

        confirmCropping(): void {
            // @ts-ignore
            const cropConfig = this.$refs.cropper.getData({rounded: true});
            if (cropConfig.width < this.MIN_WIDTH || cropConfig.height < this.MIN_HEIGHT) {
                pushVJoySnackbar({
                    props: {
                        message: this.$msg(
                            'image.edit.error.message.invalid.dimensions',
                            cropConfig.width + '*' + cropConfig.height,
                            this.MIN_WIDTH + '*' + this.MIN_HEIGHT,
                        ),
                        level: 'error',
                    },
                });
                return;
            }
            this.croppedImageDataURL = this.$refs.cropper.getCroppedCanvas().toDataURL();
            this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
                this.blob = blob;
            });
            this.cropping = false;
        }

        zoom(percent: number) {
            this.$refs.cropper.relativeZoom(percent);
        }

        async uploadNewPhoto(): Promise<void> {
            const file = this.blob || this.file;
            if (!file) {
                return;
            }
            const picture = new File([file], 'blob');

            if (file.size > this.MAX_SIZE) {
                pushVJoySnackbar({
                    props: {
                        message: this.$msg('image.edit.error.message.invalid.size', this.MAX_SIZE / 1000000),
                        level: 'error',
                    },
                });

                return;
            }
            this.loading = true;
            const [response, error] = await this.accountSummaryRepository.uploadPhoto(picture);
            this.loading = false;
            if (response) {
                await this.trackingRepository.track(`photo-uploaded-${this.$store.state.funnelMode}`);
                pushVJoySnackbar({
                    props: {
                        message: this.$msg('account.update.success'),
                    },
                });
                const [image] = (response as any).files;
                eventBusProfileCreationWizard.emit('wizard:profile-photo-updated', {image});
                await this.$refs.dialogRef.closeDialog();
            }
            if (error) {
                pushVJoySnackbar({
                    props: {
                        message: this.$msg('profile.photo.update.failed'),
                        level: 'error',
                    },
                });
            }
        }
    }
